import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { ChevronRightIcon, FormCard, PrimaryButton } from './ui'
import ClaimIncidentDamageDescribeForm from './ClaimIncidentDamageDescribeForm'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimIncidentHref,
  buildClaimOtherOtherLossReviewHref,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
} from '../claims/claimFlowModel'
import { parseOtherClaimSubtypeQuery, type OtherClaimSubtypeId } from '../claims/otherClaimSubtypeOptions'
import {
  getMultiVehicleDescribeParts,
  persistMultiVehicleDescribeNarratives,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import { useClaimFormSubmitAttempt } from '../claims/claimFormValidationFeedback'

const OTHER_LOSS_SUBTYPE = 'other-loss' as const satisfies OtherClaimSubtypeId

function initialDescribeState(): { incident: string; damage: string } {
  return getMultiVehicleDescribeParts(readClaimDraft())
}

/**
 * Other loss FNOL: incident narrative and damage details before confirm (GST is collected on review).
 */
export default function ClaimOtherOtherLossDescribeStep() {
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const [{ incident, damage }, setPair] = useState(initialDescribeState)

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      persistMultiVehicleDescribeNarratives(policyRef, incident, damage)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [damage, incident, policyRef])

  const persistNow = () => {
    persistMultiVehicleDescribeNarratives(policyRef, incident, damage)
  }

  const backToIncident = () => {
    persistNow()
    if (claimSubtype === null) return
    navigate(buildClaimIncidentHref(policyRef, typeInQuery, 'other', claimSubtype), {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const canProceed = incident.trim().length > 0 && damage.trim().length > 0
  const { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt } =
    useClaimFormSubmitAttempt(canProceed)
  const fieldErrors = useMemo(() => {
    if (!submitAttempted) return { incident: false, damage: false }
    return {
      incident: incident.trim().length === 0,
      damage: damage.trim().length === 0,
    }
  }, [submitAttempted, incident, damage])

  const handleNext = () => {
    attemptSubmit(() => {
      if (claimSubtype === null) return
      persistNow()
      const path = buildClaimOtherOtherLossReviewHref(policyRef, typeInQuery, claimSubtype)
      navigate(path, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    })
  }

  const draft = readClaimDraft()

  if (claimSubtype === null || claimSubtype !== OTHER_LOSS_SUBTYPE) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader backAriaLabel="Back" onBack={backToIncident} title="Incident and damage" />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(draft)}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft)}
            multiVehicleIncidentDescription={incident}
            multiVehicleDamageDescription={damage}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
            weatherVehicleParkedAtIncident={draft?.weatherVehicleParkedAtIncident ?? null}
            fireVehicleParkedAtIncident={null}
            theftVehicleParkedAtIncident={draft?.theftVehicleParkedAtIncident ?? null}
          />
        }
      >
        <FormCard>
          <ClaimIncidentDamageDescribeForm
            incident={incident}
            damage={damage}
            onIncidentChange={(value) => setPair((p) => ({ ...p, incident: value }))}
            onDamageChange={(value) => setPair((p) => ({ ...p, damage: value }))}
            fieldErrors={fieldErrors}
            showSubmitSummary={showSubmitSummary}
            clearSubmitAttempt={clearSubmitAttempt}
          />
        </FormCard>

        <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
          <PrimaryButton
            type="button"
            size="lg"
            fullWidth
            onClick={handleNext}
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
